import { useEffect, useRef, useState } from 'react'
import {
  CircleNotch,
  CheckCircle,
  Warning,
  X,
} from '@phosphor-icons/react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useRatePropagationStore } from '@/store/rate-propagation.store'
import { memberRateApi } from '@/lib/api/member-resources.api'

export function RatePropagationBanner() {
  const { status, updated, skipped, failed, dismissed, dismiss, retryRequested, lastContext, completePropagation } =
    useRatePropagationStore()
  const [showFailedDialog, setShowFailedDialog] = useState(false)
  const autoDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isComplete = status === 'complete'
  const hasFailures = failed.length > 0

  // Handle retry: re-run propagation with saved context when retry is requested
  useEffect(() => {
    if (!retryRequested || !lastContext) return

    memberRateApi.propagateRateChanges(lastContext).then((result) => {
      completePropagation({
        updated: result.updated,
        skipped: result.skipped,
        failed: result.failed.map((f) => ({
          entryId: f.entryId,
          memberName: f.memberName || '---',
          projectName: f.projectName || '---',
          reason: f.reason,
          date: f.date || '---',
        })),
      })
    }).catch(() => {
      completePropagation({
        updated: 0,
        skipped: 0,
        failed: [{ entryId: '', memberName: '', projectName: '', reason: 'Retry request failed', date: '' }],
      })
    })
  }, [retryRequested, lastContext, completePropagation])

  // Auto-dismiss after 10 seconds if no failures
  useEffect(() => {
    if (isComplete && !hasFailures) {
      autoDismissRef.current = setTimeout(() => {
        dismiss()
      }, 10_000)
    }
    return () => {
      if (autoDismissRef.current) clearTimeout(autoDismissRef.current)
    }
  }, [isComplete, hasFailures, dismiss])

  if (status === 'idle' || dismissed) return null

  return (
    <>
      <div className="mb-4 rounded-md border px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          {status === 'running' && (
            <>
              <CircleNotch className="h-4 w-4 animate-spin text-blue-500" />
              <span>Updating affected time entries...</span>
            </>
          )}
          {isComplete && !hasFailures && (
            <>
              <CheckCircle
                className="h-4 w-4 text-green-500"
                weight="fill"
              />
              <span>
                Updated {updated} time{' '}
                {updated === 1 ? 'entry' : 'entries'} with new rates.
              </span>
            </>
          )}
          {isComplete && hasFailures && (
            <>
              <Warning className="h-4 w-4 text-amber-500" weight="fill" />
              <span>
                Updated {updated} of {updated + failed.length} time entries.{' '}
                {failed.length} {failed.length === 1 ? 'entry' : 'entries'}{' '}
                failed.
              </span>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isComplete && hasFailures && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFailedDialog(true)}
            >
              View Failed
            </Button>
          )}
          {(isComplete || status === 'running') && (
            <button
              onClick={dismiss}
              className="p-1 hover:bg-muted rounded transition-colors"
              title="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <FailedRateUpdatesDialog
        open={showFailedDialog}
        onOpenChange={setShowFailedDialog}
        failed={failed}
      />
    </>
  )
}

function FailedRateUpdatesDialog({
  open,
  onOpenChange,
  failed,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  failed: Array<{
    entryId: string
    memberName: string
    projectName: string
    reason: string
    date: string
  }>
}) {
  const { requestRetry } = useRatePropagationStore()

  const handleRetry = () => {
    onOpenChange(false)
    requestRetry()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Failed Rate Updates</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {failed.length} time{' '}
            {failed.length === 1 ? 'entry' : 'entries'} could not be
            updated.
          </p>
        </DialogHeader>

        <div className="max-h-64 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Member</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {failed.map((entry) => (
                <TableRow key={entry.entryId}>
                  <TableCell className="text-sm">{entry.date}</TableCell>
                  <TableCell className="text-sm">
                    {entry.memberName}
                  </TableCell>
                  <TableCell className="text-sm">
                    {entry.projectName}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {entry.reason}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleRetry}>Retry All</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
